import { ComponentPropsWithoutRef, ReactNode } from 'react';
import * as Headless from '@headlessui/react';
import clsx from 'clsx';

import { Text } from './text';

const sizes = {
  xs: 'sm:max-w-xs',
  sm: 'sm:max-w-sm',
  md: 'sm:max-w-md',
  lg: 'sm:max-w-lg',
  xl: 'sm:max-w-xl',
  '2xl': 'sm:max-w-2xl',
  '3xl': 'sm:max-w-3xl',
  '4xl': 'sm:max-w-4xl',
  '5xl': 'sm:max-w-5xl',
};

/**
 * -----------------------------------------------------------------------------
 * Re-usable alert component
 */
export function Alert({
  size = 'md',
  className,
  children,
  ...props
}: {
  size?: keyof typeof sizes;
  className?: string;
  children: ReactNode;
} & Omit<Headless.DialogProps, 'as' | 'className'>) {
  return (
    <Headless.Dialog {...props}>
      <Headless.DialogBackdrop
        className="
          bg-gray-950/15 data-closed:opacity-0 data-enter:ease-out data-leave:ease-in fixed inset-0 flex
          w-screen justify-center overflow-y-auto px-2 py-2 transition
          duration-100 focus:outline-0 dark:bg-gray-950/50 sm:px-6 sm:py-8 lg:px-8
          lg:py-16
        "
        transition
      />

      <div className="fixed inset-0 w-screen overflow-y-auto pt-6 sm:pt-0">
        <div
          className="
          grid min-h-full grid-rows-[1fr_auto_1fr] justify-items-center p-8
          sm:grid-rows-[1fr_auto_3fr] sm:p-4
        "
        >
          <Headless.DialogPanel
            className={clsx(
              className,
              sizes[size],
              'row-start-2 w-full rounded-2xl bg-white p-8 shadow-lg ring-1',
              'ring-gray-950/10 dark:bg-gray-900 sm:rounded-2xl sm:p-6',
              'forced-colors:outline dark:ring-white/10',
              'data-closed:opacity-0 transition duration-100 will-change-transform',
              'data-enter:ease-out data-closed:data-enter:scale-95 data-leave:ease-in'
            )}
            transition
          >
            {children}
          </Headless.DialogPanel>
        </div>
      </div>
    </Headless.Dialog>
  );
}

/**
 * Title of the alert.
 */
export function AlertTitle({
  className,
  ...props
}: { 
  /** Optional CSS class name. */
  className?: string 
} & Omit<
  Headless.DialogTitleProps,
  'as' | 'className'
>) {
  return (
    <Headless.DialogTitle
      {...props}
      className={clsx(
        className,
        'text-balance text-center text-base/6 font-semibold text-gray-950',
        'sm:text-wrap dark:text-white sm:text-start sm:text-sm/6'
      )}
    />
  );
}

/**
 * Description text for the alert.
 */
export function AlertDescription({
  className,
  ...props
}: { 
  /** Optional CSS class name. */
  className?: string 
} & Omit<
  Headless.DescriptionProps<typeof Text>,
  'as' | 'className'
>) {
  return (
    <Headless.Description
      as={Text}
      {...props}
      className={clsx(className, 'text-pretty mt-2 text-center sm:text-start')}
    />
  );
}

/**
 * Container for the main content of the alert.
 */
export function AlertBody({
  className,
  ...props
}: ComponentPropsWithoutRef<'div'>) {
  return <div {...props} className={clsx(className, 'mt-4')} />;
}

/**
 * Container for the action buttons at the bottom of the alert.
 */
export function AlertActions({
  className,
  ...props
}: ComponentPropsWithoutRef<'div'>) {
  return (
    <div
      {...props}
      className={clsx(
        className,
        '*:w-full mt-6 flex flex-col-reverse items-center justify-end gap-3',
        'sm:*:w-auto sm:mt-4 sm:flex-row'
      )}
    />
  );
}
